import _object_spread from "@swc/helpers/src/_object_spread.mjs";
import _object_spread_props from "@swc/helpers/src/_object_spread_props.mjs";
import { useEffect, useRef } from 'react';
import c from 'classnames';
import omit from 'lodash/omit';
import pick from 'lodash/pick';
import s from './common.scss';
/*
  Read the comments below before reading this.

  Why all this instead of compartmentalizing the content props into a `contentProps` object? Because this is a case
  where the consumer doesn't need to know the internal workings of the component. In other odd cases, we pass through
  fields like `labelClassName` that may be passed down through a couple component levels. This inherently requires a
  knowledge of what's going on inside the component. We aren't doing that here. These are all important props that should
  be exposed at the base of the ListItem API and are inherent to what a ListItem is.

  The approach below keeps all the ListItemContentProps compartmentalized inside the ListItem code without requiring the user
  to pass in a separate object.
*/ /**
 * Each ListItem implementation requires the ListItemContent component. Separating out the props in each implementation
 * will get unwieldy and is prone to a lot of accidental errors or annoying component differences. So this variable keeps
 * of all the ListItemContent props that we pass through from each ListItem implementation as is.
 */ const passThroughContentProps = [
    'label',
    'subLabel',
    'bottomSlot',
    'prefix',
    'prefixIcon',
    'suffix',
    'suffixIcon',
    'isLabelBold'
];
const propDefaults = {};
/**
 * These are present in the user-facing API, but aren't used in disparate ListItem implementations. They're mostly used
 * in shared ListItem code. Like when we fetch the initial root component `className`.
 */ const commonPropsToStrip = [
    'noLeftPadding',
    'showSuffixOnlyOnHover',
    'fillContainerWidth',
    'focused'
];
/**
 * Fetchs all the {@link ListItemContentProps} out of the main props object so that it's easy to pass them along to the
 * ListItemContent component.
 */ function getContentProps(propsWithoutDefaults) {
    const props = _object_spread({}, propDefaults, propsWithoutDefaults);
    return _object_spread_props(_object_spread({}, pick(props, passThroughContentProps)), {
        isDim: props.disabled
    });
}
/**
 * Omits all {@link ListItemContentProps} from the main props object. Useful in cases where we pass the `{...rest}` to the
 * component wrapping our ListItemContent.
 *
 * e.g.
 * ```
 *  [...]
 *  const radioProps = getRootProps(props)
 *  return (
 *    <RadioButton {...radioProps}>
 *      <ListItemContent {...contentProps}/>
 *    </RadioButton>
 *  )
 * ```
 */ function getRootProps(propsWithoutDefaults, isInteractive) {
    const props = _object_spread({}, propDefaults, propsWithoutDefaults);
    return _object_spread_props(_object_spread({}, omit(props, [
        // These are only relevant for the content component, so the root component doesn't
        // need to worry about it.
        ...passThroughContentProps,
        // These are only relevant to common ListItem code, like the `getRootClassNames` function.
        // Removing them keeps us from accidentally passing an invalid prop to a DOM element
        ...commonPropsToStrip
    ])), {
        className: getRootClassNames(props, isInteractive)
    });
}
function getRootClassNames({ disabled , noLeftPadding , fillContainerWidth , showSuffixOnlyOnHover , className  }, isInteractive) {
    return c(s['root'], isInteractive && s['interactive'], disabled && s['disabled'], noLeftPadding && s['no-left-padding'], fillContainerWidth && s['fill-container-width'], showSuffixOnlyOnHover && s['suffix-on-hover'], className);
}
/**
 * Scrolls to the element that the supplied `ref` points to, assuming that the supplied props object has
 * a `focused` prop set to true.
 */ function useFocusedPropHandler(ref, props) {
    useEffect(()=>{
        if (ref.current && props.focused) {
            ref.current.focus({
                preventScroll: true
            });
        }
    }, [
        ref,
        props.focused
    ]);
}
/**
 * Takes a ref + props type, a props object, and returns a set of variables for easy use inside any ListItem implementation.
 */ export function useListItem(props, /**
   * Only false for implementations that require no interactive feedback. e.g. plain divs with no onClick handling
   */ isInteractive = true) {
    const contentProps = getContentProps(props);
    const rootProps = getRootProps(props, isInteractive);
    const ref = useRef(null);
    useFocusedPropHandler(ref, props);
    return {
        /**
     * Props that -- in most cases -- should be spread onto the root component of your ListItem implementation.
     */ rootProps,
        /**
     * Props that should be spread onto the content component of your ListItem implementation.
     */ contentProps,
        // `ref` is separate from `remainingProps` for typescript reasons. Could maybe be rolled into `remainingProps` with the right types if you want
        // to dive into it.
        /**
     * Plop this on your root tag.
     */ ref
    };
}
